// src/components/Navbar/Navbar.jsx
import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";

// Icons
import { FaEnvelope, FaBars, FaTimes, FaChevronDown } from "react-icons/fa";

// Assets
import logoWebp from "../../assets/logos/logo.webp";
import GlobeSelector from "../Global/GlobalSelector";

// Data

const navLinks = [
  {
    label: "Who We Are",
    path: "/about",
    children: [{ label: "About Us", path: "/about" }],
  },
  {
    label: "What We Do",
    path: "#",
    type: "mega",
    children: [
      {
        category: "Industries",
        items: [
          { label: "Technology", path: "/industries/technology" },
          { label: "Engineering", path: "/industries/engineering" },
          { label: "Manufacturing", path: "/industries/manufacturing" },
          { label: "Energy", path: "/industries/energy" },
          { label: "Logistics", path: "/industries/logistics" },
          { label: "Healthcare", path: "/industries/healthcare" },
          { label: "Retail & FMCG", path: "/industries/retail-and-fmcg" },
          { label: "Finance", path: "/industries/finance" },
        ],
      },
      {
        category: "Services",
        items: [
          { label: "Temporary Staffing", path: "/services/temporarystaffing" },
          { label: "Permanent Recruitment", path: "/services/permanentrecruitment" },
          { label: "Contract Staffing", path: "/services/contractstaffing" },
          { label: "Payroll Management", path: "/services/payrollmanagement" },
          { label: "RPO", path: "/services/rpo" },
          { label: "Compliance Support", path: "/services/compliancesupport" },
        ],
      },
    ],
  },
  { label: "Careers", path: "/careers" },
  { label: "Contact", path: "/contact" },
  {
    label: "Insights",
    path: "#",
    children: [
      { label: "Blogs", path: "/blogs" },
      { label: "Events", path: "/events" },
    ],
  },
  { label: "Gallery", path: "/gallery" },
];

/* =========================================================================
   Components
   ========================================================================= */

// --- Mobile Submenu Item (Accordion) ---
const MobileNavItem = React.memo(({ item, onClickLink }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;

  const toggleOpen = React.useCallback(() => setIsOpen((prev) => !prev), []);

  const textColor = "text-slate-100";
  const subMenuBg = "bg-white/5 text-slate-300";

  return (
    <div className="border-b border-white/5 last:border-none">
      <div className="flex items-center">
        {hasChildren ? (
          <button
            onClick={toggleOpen}
            className={`flex-1 text-left py-4 px-5 ${textColor} font-medium flex items-center justify-between transition-colors`}
          >
            {item.label}
            <motion.span
              animate={{ rotate: isOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="flex items-center"
            >
              <FaChevronDown className="text-xs text-[#70be44]" />
            </motion.span>
          </button>
        ) : (
          <NavLink
            to={item.path}
            onClick={onClickLink}
            className={({ isActive }) =>
              `flex-1 block py-4 px-5 font-medium transition-colors ${isActive ? "text-[#70be44] bg-[#70be44]/10" : `${textColor} hover:text-[#70be44]`
              }`
            }
          >
            {item.label}
          </NavLink>
        )}
      </div>

      <AnimatePresence>
        {isOpen && hasChildren && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className={`${subMenuBg} overflow-hidden shadow-inner`}
          >
            <div className="flex flex-col pb-2">
              {item.type === "mega"
                ? item.children.map((col, idx) => (
                  <div key={idx} className="px-5 py-2">
                    {/* Category Header */}
                    <h4 className="text-[#70be44] text-[10px] uppercase tracking-wider font-bold mb-2 mt-2 border-b border-white/10 pb-1 w-max">{col.category}</h4>
                    {col.items.map((sub) => (
                      <NavLink
                        key={sub.path}
                        to={sub.path}
                        onClick={onClickLink}
                        className={({ isActive }) =>
                          `block py-2 px-2 text-sm border-b border-dashed border-white/5 last:border-none ${isActive
                            ? "text-[#70be44] font-semibold"
                            : "text-slate-400 hover:text-[#70be44] hover:pl-3 transition-all"
                          }`
                        }
                      >
                        {sub.label}
                      </NavLink>
                    ))}
                  </div>
                ))
                : item.children.map((sub) => (
                  <NavLink
                    key={sub.path}
                    to={sub.path}
                    onClick={onClickLink}
                    className={({ isActive }) =>
                      `block py-3 px-8 text-sm border-b border-white/5 last:border-none ${isActive
                        ? "text-[#70be44] font-semibold"
                        : "text-slate-400 hover:bg-white/5 hover:text-[#70be44] hover:pl-10 transition-all"
                      }`
                    }
                  >
                    {sub.label}
                  </NavLink>
                ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

MobileNavItem.displayName = "MobileNavItem";

// --- Desktop Nav Item ---
const DesktopNavItem = React.memo(({ item, isLast }) => {
  const [hover, setHover] = useState(false);
  const location = useLocation();

  const handleMouseEnter = React.useCallback(() => setHover(true), []);
  const handleMouseLeave = React.useCallback(() => setHover(false), []);
  const handleClose = React.useCallback(() => setHover(false), []);

  const isActive =
    item.path === location.pathname ||
    (item.children &&
      item.children.some((child) =>
        child.items
          ? child.items.some((sub) => sub.path === location.pathname)
          : child.path === location.pathname
      ));

  const textColor = "text-white";

  return (
    <li
      className="relative h-full flex items-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <NavLink
        to={item.path}
        className={`px-4 py-3 text-[14px] font-bold uppercase tracking-wide transition-all flex items-center gap-1 font-poppins ${isActive ? "text-[#70be44]" : `${textColor} hover:text-[#70be44]`
          }`}
        onClick={(e) => item.children && e.preventDefault()}
      >
        {item.label}
        {item.children && <FaChevronDown className="text-[10px]" />}
      </NavLink>

      {/* Dropdown / Mega Menu */}
      <AnimatePresence>
        {hover && item.children && (
          <motion.div
            initial={{ opacity: 0, y: 15, x: item.type === "mega" ? "-50%" : "0%" }}
            animate={{ opacity: 1, y: 0, x: item.type === "mega" ? "-50%" : "0%" }}
            exit={{ opacity: 0, y: 15, x: item.type === "mega" ? "-50%" : "0%" }}
            transition={{ duration: 0.2 }}
            className={`absolute top-full cursor-default bg-white backdrop-blur-xl shadow-2xl border-t-[3px] border-[#70be44] rounded-b-2xl overflow-hidden z-50 ${item.type === "mega"
              ? "left-1/2 w-[650px] p-8 grid grid-cols-2 gap-8"
              : isLast
                ? "right-0 min-w-[240px] py-1 flex flex-col items-start"
                : "left-0 min-w-[240px] py-1 flex flex-col"
              }`}
          >
            {item.type === "mega" ? (
              // Mega Menu Content
              item.children.map((col) => (
                <div key={col.category}>
                  <h4 className="text-[#70be44] font-bold text-[11px] uppercase tracking-wider border-b border-gray-100 pb-2 mb-4">{col.category}</h4>
                  <ul className="space-y-2">
                    {col.items.map(sub => (
                      <li key={sub.path}>
                        <Link
                          to={sub.path}
                          className="block text-slate-700 hover:text-[#70be44] hover:translate-x-1 transition-all text-sm font-medium"
                          onClick={handleClose}
                        >
                          {sub.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            ) : (
              // Simple Dropdown Content
              item.children.map(sub => (
                <Link
                  key={sub.path}
                  to={sub.path}
                  className="block w-full text-left px-6 py-3 text-sm font-medium text-slate-700 hover:text-[#70be44] hover:bg-gray-50 hover:pl-7 transition-all"
                  onClick={handleClose}
                >
                  {sub.label}
                </Link>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
});

DesktopNavItem.displayName = "DesktopNavItem";

/* =========================================================================
   Main Navbar Component
   ========================================================================= */
const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();



  // Scroll Listener for Navbar Transparency
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [menuOpen]);
  // Handlers
  const toggleMenu = React.useCallback(() => setMenuOpen((prev) => !prev), []);
  const closeMenu = React.useCallback(() => setMenuOpen(false), []);

  const isHomePage = location.pathname === "/";
  const navClass = isHomePage 
    ? (scrolled ? "bg-slate-950/95 backdrop-blur-md py-3 shadow-lg" : "bg-transparent py-4") 
    : "bg-slate-950 py-3 shadow-lg";

  return (
    <header className={`fixed top-0 left-0 w-full z-[1002] transition-all duration-300 font-poppins ${navClass}`}>
      <div className="max-w-[1440px] w-[95%] mx-auto flex items-center justify-between relative transition-all duration-300">

        {/* Logo */}
        <Link to="/" className="flex-shrink-0 transition-all duration-300">
          <div className="relative inline-block py-2">
            <LazyLoadImage
              src={logoWebp}
              alt="Minivel Services"
              effect="blur"
              height={60}
              width={160}
              className="object-contain transition-all duration-300 h-[60px]"
            />
          </div>
        </Link>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden p-2 border-2 rounded-md transition-all duration-300 border-white/40 text-white hover:bg-white/10"
          onClick={toggleMenu}
          aria-label="Toggle Menu"
        >
          {menuOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden md:block h-full">
          <ul className="flex items-center h-full gap-5">
            {navLinks.map((item, index) => (
              <DesktopNavItem
                key={item.label}
                item={item}
                isLast={index >= navLinks.length - 2}
              />
            ))}
          </ul>
        </nav>
      </div>

      {/* --- Mobile Menu Overlay --- */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed top-[80px] left-0 w-full h-[calc(100vh-80px)] z-[1001] bg-slate-950/95 backdrop-blur-2xl overflow-y-auto md:hidden shadow-inner"
          >
            <div className="flex flex-col pb-20">
              {navLinks.map(item => (
                <MobileNavItem 
                  key={item.label} 
                  item={item} 
                  onClickLink={closeMenu} 
                />
              ))}

              {/* Mobile Footer Links */}
              <div className="mt-8 px-5 pt-8 border-t border-gray-100 bg-gray-50 pb-10">
                <Link to="/employer" className="block text-[#70be44] font-bold uppercase mb-4 text-sm" onClick={closeMenu}>For Employers</Link>
                <a href="mailto:info@minivel.com" className="flex items-center gap-2 text-slate-600 text-sm mb-4 hover:text-[#70be44]">
                  <FaEnvelope /> info@minivel.com
                </a>
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <GlobeSelector />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </header>
  );
};

export default Navbar;
